import React, { useEffect, useState } from 'react';
import { clientService, ClientDTO } from '../../services/clientApi';
import ScheduleApiService from '../../services/scheduleApi';
import { Appointment } from '../Calendar/types';
import { Modal } from '../UI/Modal';
import { formatClientDate, getDisplayTags } from '../../utils/clientDisplay';
import {
  buildGoogleEmbedUrl,
  buildGoogleViewUrl,
  buildOsmEmbedUrl,
  buildOsmViewUrl,
  getClientCoordinates,
  type MapProvider,
} from '../../utils/mapUtils';
import { getUserPreferences, subscribeUserPreferences, UserPreferences } from '../../services/userPreferences';

interface ClientDetailsModalProps {
  client: ClientDTO | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (client: ClientDTO) => void;
  onDelete: (client: ClientDTO) => void | Promise<void>;
  onSchedule: (client: ClientDTO) => void;
}

const displayValue = (value?: string | number) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  return value;
};

const APPOINTMENT_STATUS: Record<string, { label: string; className: string }> = {
  scheduled: { label: 'Planifié', className: 'bg-blue-100 text-blue-800' },
  confirmed: { label: 'Confirmé', className: 'bg-emerald-100 text-emerald-800' },
  in_progress: { label: 'En cours', className: 'bg-yellow-100 text-yellow-800' },
  completed: { label: 'Terminé', className: 'bg-gray-100 text-gray-700' },
  cancelled: { label: 'Annulé', className: 'bg-red-100 text-red-700' },
  no_show: { label: 'Absent', className: 'bg-orange-100 text-orange-700' },
};

export const ClientDetailsModal: React.FC<ClientDetailsModalProps> = ({
  client,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onSchedule
}) => {
  const [detailedClient, setDetailedClient] = useState<ClientDTO | null>(client);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [mapProvider, setMapProvider] = useState<MapProvider>('google');
  const [isMapFallbackEnabled, setIsMapFallbackEnabled] = useState(true);
  const [mapMessage, setMapMessage] = useState<string | null>(null);

  useEffect(() => {
    const applyPreferences = (preferences: UserPreferences) => {
      setMapProvider(preferences.mapProvider);
      setIsMapFallbackEnabled(preferences.enableMapFallback);
      setMapMessage(null);
    };

    applyPreferences(getUserPreferences());
    const unsubscribe = subscribeUserPreferences(applyPreferences);

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const preferences = getUserPreferences();
    setDetailedClient(client);
    setIsMapVisible(false);
    setMapProvider(preferences.mapProvider);
    setIsMapFallbackEnabled(preferences.enableMapFallback);
    setMapMessage(null);

    if (!isOpen || !client?.id) {
      setIsLoadingDetails(false);
      setDetailsError(null);
      return;
    }

    const clientId = client.id;

    let isMounted = true;

    const loadClientDetails = async () => {
      setIsLoadingDetails(true);
      setDetailsError(null);

      try {
        const response = await clientService.getClientById(clientId);

        if (isMounted && response.success) {
          setDetailedClient(response.data);
        }
      } catch (error: any) {
        if (isMounted) {
          console.error('Erreur lors du chargement du client:', error);
          setDetailsError(error.response?.data?.message || 'Impossible de charger la fiche client');
        }
      } finally {
        if (isMounted) {
          setIsLoadingDetails(false);
        }
      }
    };

    loadClientDetails();

    return () => {
      isMounted = false;
    };
  }, [client, isOpen]);

  // Charger les rendez-vous du client
  useEffect(() => {
    setAppointments([]);

    if (!isOpen || !client?.id) {
      setIsLoadingAppointments(false);
      return;
    }

    const clientId = client.id;
    let isMounted = true;

    const loadAppointments = async () => {
      setIsLoadingAppointments(true);
      try {
        const result = await ScheduleApiService.getSchedule({ clientId });
        if (isMounted) {
          const sorted = (result.appointments || []).sort(
            (a, b) =>
              new Date(String(b.startTime)).getTime() -
              new Date(String(a.startTime)).getTime()
          );
          setAppointments(sorted);
        }
      } catch (error) {
        // Pas d'affichage d'erreur — la section est optionnelle
        console.error('Erreur lors du chargement des rendez-vous du client:', error);
      } finally {
        if (isMounted) {
          setIsLoadingAppointments(false);
        }
      }
    };

    loadAppointments();

    return () => {
      isMounted = false;
    };
  }, [client?.id, isOpen]);

  if (!client) {
    return null;
  }

  const activeClient = detailedClient || client;
  const displayTags = getDisplayTags(activeClient);
  const coordinates = getClientCoordinates(activeClient);
  const hasCoordinates = coordinates !== null;

  let mapEmbedUrl = '';
  let mapViewUrl = '';

  if (coordinates) {
    if (mapProvider === 'google') {
      mapEmbedUrl = buildGoogleEmbedUrl(coordinates.lat, coordinates.lng);
      mapViewUrl = buildGoogleViewUrl(coordinates.lat, coordinates.lng);
    } else {
      mapEmbedUrl = buildOsmEmbedUrl(coordinates.lat, coordinates.lng);
      mapViewUrl = buildOsmViewUrl(coordinates.lat, coordinates.lng);
    }
  }

  const handleToggleMap = () => {
    setIsMapVisible((previous) => {
      const next = !previous;
      if (next) {
        const preferences = getUserPreferences();
        setMapProvider(preferences.mapProvider);
        setIsMapFallbackEnabled(preferences.enableMapFallback);
        setMapMessage(null);
      }
      return next;
    });
  };

  const handleMapLoadError = () => {
    if (mapProvider === 'google' && isMapFallbackEnabled) {
      setMapProvider('osm');
      setMapMessage('Google Maps indisponible, bascule vers OpenStreetMap.');
      return;
    }

    if (mapProvider === 'google' && !isMapFallbackEnabled) {
      setMapMessage('Google Maps indisponible et fallback désactivé dans vos paramètres.');
      return;
    }

    setMapMessage('La carte est indisponible pour le moment.');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Informations du client"
      size="lg"
    >
      <div className="space-y-6">
        {detailsError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {detailsError}
          </div>
        )}

        <div className="flex items-start gap-4 rounded-xl bg-slate-50 p-4">
          <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-lg font-semibold text-blue-700">
            {activeClient.firstName[0]}{activeClient.lastName[0]}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-xl font-semibold text-gray-900">
                {activeClient.firstName} {activeClient.lastName}
              </h3>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  activeClient.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {activeClient.isActive ? 'Actif' : 'Inactif'}
              </span>
              {isLoadingDetails && (
                <span className="text-xs font-medium text-blue-700">Actualisation...</span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-600">{displayValue(activeClient.company)}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {displayTags.length > 0 ? displayTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800"
                >
                  {tag}
                </span>
              )) : (
                <span className="text-sm text-gray-500">Aucun tag</span>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <section className="rounded-xl border border-gray-200 p-4">
            <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Contact</h4>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Email</dt>
                <dd className="text-gray-900">{displayValue(activeClient.email)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Téléphone</dt>
                <dd className="text-gray-900">{displayValue(activeClient.phone)}</dd>
              </div>
            </dl>
          </section>

          <section className="rounded-xl border border-gray-200 p-4">
            <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Visites</h4>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Durée par défaut</dt>
                <dd className="text-gray-900">{displayValue(activeClient.defaultVisitDuration)} min</dd>
              </div>
              <div>
                <dt className="text-gray-500">Créé le</dt>
                <dd className="text-gray-900">{formatClientDate(activeClient.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Mis à jour le</dt>
                <dd className="text-gray-900">{formatClientDate(activeClient.updatedAt)}</dd>
              </div>
            </dl>
          </section>
        </div>

        <section className="rounded-xl border border-gray-200 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Adresse</h4>
            {hasCoordinates && (
              <button
                type="button"
                onClick={handleToggleMap}
                className="rounded-lg border border-blue-300 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
              >
                {isMapVisible ? 'Masquer la carte' : 'Afficher la carte'}
              </button>
            )}
          </div>
          <div className="space-y-1 text-sm text-gray-900">
            <p>{displayValue(activeClient.address)}</p>
            <p>
              {displayValue(activeClient.postalCode)} {displayValue(activeClient.city)}
            </p>
            <p>{displayValue(activeClient.country || 'France')}</p>
            {hasCoordinates && (
              <p className="text-xs text-gray-500">
                GPS: {coordinates?.lat.toFixed(6)}, {coordinates?.lng.toFixed(6)}
              </p>
            )}
            {!hasCoordinates && (
              <p className="text-xs text-amber-700">Aucune coordonnée GPS disponible pour ce client.</p>
            )}
          </div>

          {hasCoordinates && isMapVisible && (
            <div className="mt-3 overflow-hidden rounded-lg border border-gray-200">
              <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                Provider carte: {mapProvider === 'google' ? 'Google Maps' : 'OpenStreetMap'}
              </div>
              <iframe
                title={`Carte de ${activeClient.firstName} ${activeClient.lastName}`}
                src={mapEmbedUrl}
                className="h-56 w-full"
                loading="lazy"
                onError={handleMapLoadError}
              />
              {mapMessage && (
                <div className="border-t border-gray-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {mapMessage}
                </div>
              )}
              <div className="border-t border-gray-200 bg-gray-50 px-3 py-2 text-right">
                <a
                  href={mapViewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-medium text-blue-700 hover:underline"
                >
                  {mapProvider === 'google' ? 'Ouvrir dans Google Maps' : 'Ouvrir dans OpenStreetMap'}
                </a>
              </div>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 p-4">
          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Notes</h4>
          <p className="whitespace-pre-wrap text-sm text-gray-900">{displayValue(activeClient.notes)}</p>
        </section>

        <section className="rounded-xl border border-gray-200 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Rendez-vous récents</h4>
            {isLoadingAppointments && (
              <span className="text-xs font-medium text-blue-700">Chargement...</span>
            )}
          </div>
          {!isLoadingAppointments && appointments.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun rendez-vous enregistré.</p>
          ) : (
            <ul className="space-y-2">
              {appointments.slice(0, 5).map((appt) => {
                const apptDate = new Date(String(appt.startTime));
                const statusInfo = APPOINTMENT_STATUS[appt.status] ?? {
                  label: appt.status,
                  className: 'bg-gray-100 text-gray-700',
                };
                return (
                  <li key={appt.id} className="flex items-start gap-3 rounded-lg bg-gray-50 p-3 text-sm">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-gray-900">
                        {appt.title || 'Rendez-vous'}
                      </p>
                      <p className="mt-0.5 text-gray-500">
                        {new Intl.DateTimeFormat('fr-FR', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        }).format(apptDate)}
                        {' · '}
                        {appt.duration} min
                      </p>
                    </div>
                    <span
                      className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusInfo.className}`}
                    >
                      {statusInfo.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => onSchedule(activeClient)}
            className="rounded-lg border border-emerald-300 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
          >
            Planifier un rendez-vous
          </button>
          <button
            type="button"
            onClick={() => onDelete(activeClient)}
            className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
          >
            Supprimer
          </button>
          <button
            type="button"
            onClick={() => onEdit(activeClient)}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Modifier
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  );
};